import { ReactNode } from "react";
import { Alert, Box, Typography } from "@mui/material";
import {
  CountryMarketAccessLifecycle,
  CountryPartyType
} from "../../../../../api/Api";
import { partyLabel } from "./utils";

type PartyRowsProps<TRow extends { partyType?: CountryPartyType }> = {
  title: string;
  rows: TRow[];
  marketAccess?: CountryMarketAccessLifecycle;
  /** Shown instead of the rows when Market Access has selected nothing for this block. */
  emptyMessage: string;
  renderRow: (row: TRow) => ReactNode;
};

/**
 * One bordered block per party, headed by the party name Market Access gave it. Every downstream
 * lifecycle renders its per-party work through this, so Compliance, Legal, Treasury and Finance all
 * group the same way and in the same order.
 */
export const PartyRows = <TRow extends { partyType?: CountryPartyType }>({
  title,
  rows,
  marketAccess,
  emptyMessage,
  renderRow
}: PartyRowsProps<TRow>) => (
  <Box display="flex" flexDirection="column" gap={1.5}>
    <Typography variant="subtitle1" fontWeight={600}>
      {title}
    </Typography>

    {rows.length === 0 ? (
      <Alert severity="info" variant="outlined">
        {emptyMessage}
      </Alert>
    ) : (
      rows.map((row) => (
        <Box
          key={row.partyType}
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            padding: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            {partyLabel(row.partyType as CountryPartyType, marketAccess)}
          </Typography>
          {renderRow(row)}
        </Box>
      ))
    )}
  </Box>
);
